import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, FieldText, Modal, ModalBody, ModalFoot, ModalHead, Text, } from '@dixa/lib-shared-habla';
import { useCallback, useState } from 'react';
import { useUpload } from 'apis/uploads';
import { analytics } from 'app/utils/segment';
import dialogStyles from 'components/shared/announcementPicker/dialog.module.scss';
import { AudioPlayer } from 'components/shared/audioPlayer/audioPlayer';
import { RecordPlayer } from 'components/shared/recordPlayer/recordPlayer';
import { UploadDropzone } from 'components/shared/uploadDropzone';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { bytesToSize } from 'utils/bytesToSize';
import styles from './audioMessageModal.module.scss';
const maxFileSize = 20 * 1024 * 1024; // ~20MByte
const allowedFileTypes = 'audio/*';
function getFile(file, name) {
    return new File([file], name, { type: file.type });
}
export const AudioMessageModal = ({ modalType, onClose, onUploadFinish, openingHourType, title, }) => {
    const [file, setFile] = useState(null);
    const [name, setName] = useState('');
    const [upload, { asyncStatus }] = useUpload();
    const notify = useNotifications();
    const handleOnDrop = useCallback((file) => {
        setFile(file);
    }, []);
    const handleDropReject = useCallback((file, reason) => {
        switch (reason) {
            case 'size':
                notify({
                    title: 'File too large',
                    text: `Maximum upload file size is ${bytesToSize(maxFileSize)}. File size on "${file.name}" is ${bytesToSize(file.size)}`,
                    time: 5000,
                }, NotificationType.Error);
                break;
            case 'type':
                notify({
                    title: 'Unsupported file type',
                    text: 'Only audio files are supported',
                    time: 5000,
                }, NotificationType.Error);
                break;
        }
    }, [notify]);
    const handleNameChange = useCallback((event) => void setName((event === null || event === void 0 ? void 0 : event.target.value) || ''), []);
    const handleConfirmClick = useCallback(() => {
        if (!file)
            return;
        analytics.businessHoursScheduleAudioMessageFormSubmitted({
            openingHourType,
            type: modalType,
        });
        upload({
            file: getFile(file, name),
            type: 'announcement',
        }).then((state) => {
            switch (state.asyncStatus) {
                case 'fulfilled':
                    onUploadFinish(getFile(file, name), state.url);
                    break;
                case 'rejected':
                    notify({ title: 'Failed to fetch audio items, please try again' }, NotificationType.Error);
                    break;
            }
        });
    }, [file, modalType, name, openingHourType, upload, notify, onUploadFinish]);
    const handleClose = useCallback(() => {
        analytics.businessHoursScheduleAudioMessageFormCancelled({
            openingHourType,
            type: modalType,
        });
        onClose();
    }, [modalType, openingHourType, onClose]);
    const isRecordModal = modalType === 'record';
    const isAudioPlayerShown = !isRecordModal && file;
    const isUploadShown = !isRecordModal && !file;
    return (_jsx(Modal, Object.assign({ isOpen: true, onClose: handleClose }, { children: _jsxs(Box, Object.assign({ width: "600px" }, { children: [_jsx(ModalHead, { onClose: handleClose, title: title }), _jsxs(ModalBody, { children: [_jsx(Text.Paragraph, Object.assign({ className: styles.description }, { children: openingHourType === 'regular'
                                ? 'This audio message can be played when your customers call you outside your standard business hours.'
                                : 'This audio message can be played when your customers call you outside these business hours.' })), isRecordModal && _jsx(RecordPlayer, { onChange: setFile, name: name }), isAudioPlayerShown && (_jsx(AudioPlayer, { src: file.preview, filename: title, onDelete: () => setFile(null) })), isUploadShown && (_jsx(UploadDropzone, { maxFileSize: maxFileSize, className: dialogStyles.dropzone, allowedFileTypes: allowedFileTypes, onDrop: handleOnDrop, onReject: handleDropReject })), _jsx(FieldText, { className: styles.nameField, input: {
                                value: name,
                                name: 'name',
                                onChange: handleNameChange,
                            }, label: "Name", placeholder: openingHourType === 'regular'
                                ? 'E.g. After hours message'
                                : 'E.g. Happy New Year, Summer holidays message etc.', required: true })] }), _jsx(ModalFoot, { cancelOnClick: handleClose, confirmOnClick: handleConfirmClick, confirmProps: {
                        disabled: !file || !name,
                        isLoading: asyncStatus === 'pending',
                    } })] })) })));
};
